import React, { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, MessageCircle, Phone, ArrowLeft } from 'lucide-react';

const ClientRegister = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    line_id: '',
    whatsapp: '',
    phone: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      toast.error('Name and Email are required');
      return;
    }

    if (!formData.line_id && !formData.whatsapp) {
      toast.error('Please provide either Line ID or WhatsApp number');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/clients/register`, formData);
      toast.success('Registration successful! Welcome to NOK Thailand Property');
      setTimeout(() => navigate('/'), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center p-4 relative">
      {/* Subtle background accent */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-[#D4AF37]/3 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Back button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[#94A3B8] hover:text-[#D4AF37] mb-8 transition-colors duration-300"
          data-testid="back-to-home-link"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to Home</span>
        </button>

        {/* Card */}
        <div className="bg-[#131A38] border border-white/8 rounded-2xl p-8 md:p-10">
          {/* Header */}
          <div className="mb-8">
            <p className="overline text-[#D4AF37] mb-3">Client Registration</p>
            <h1 className="font-heading text-3xl md:text-4xl font-semibold text-white tracking-tight leading-tight">
              Join NOK Thailand
            </h1>
            <p className="text-[#94A3B8] mt-3 text-sm leading-relaxed">
              Register to receive exclusive property updates and personalized service from our team.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            {/* Name */}
            <div>
              <Label htmlFor="name" className="text-[#94A3B8] text-xs uppercase tracking-wider font-medium mb-2 block">
                Full Name *
              </Label>
              <Input
                id="name"
                data-testid="register-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter your full name"
                required
                className="bg-[#0A0E27] border-white/10 text-white placeholder:text-[#475569] focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 h-12 rounded-xl"
              />
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email" className="text-[#94A3B8] text-xs uppercase tracking-wider font-medium mb-2 block">
                Email Address *
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3.5 w-4 h-4 text-[#475569]" />
                <Input
                  id="email"
                  type="email"
                  data-testid="register-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="you@example.com"
                  required
                  className="pl-10 bg-[#0A0E27] border-white/10 text-white placeholder:text-[#475569] focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 h-12 rounded-xl"
                />
              </div>
            </div>

            {/* Line ID */}
            <div>
              <Label htmlFor="line_id" className="text-[#94A3B8] text-xs uppercase tracking-wider font-medium mb-2 block">
                Line ID
              </Label>
              <div className="relative">
                <MessageCircle className="absolute left-3 top-3.5 w-4 h-4 text-[#475569]" />
                <Input
                  id="line_id"
                  data-testid="register-line-input"
                  value={formData.line_id}
                  onChange={(e) => setFormData({ ...formData, line_id: e.target.value })}
                  placeholder="@yourlineid"
                  className="pl-10 bg-[#0A0E27] border-white/10 text-white placeholder:text-[#475569] focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 h-12 rounded-xl"
                />
              </div>
              <p className="text-[#475569] text-xs mt-1.5">Preferred for quick contact</p>
            </div>

            {/* WhatsApp */}
            <div>
              <Label htmlFor="whatsapp" className="text-[#94A3B8] text-xs uppercase tracking-wider font-medium mb-2 block">
                WhatsApp Number
              </Label>
              <div className="relative">
                <Phone className="absolute left-3 top-3.5 w-4 h-4 text-[#475569]" />
                <Input
                  id="whatsapp"
                  data-testid="register-whatsapp-input"
                  value={formData.whatsapp}
                  onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                  placeholder="+66 XX XXX XXXX"
                  className="pl-10 bg-[#0A0E27] border-white/10 text-white placeholder:text-[#475569] focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 h-12 rounded-xl"
                />
              </div>
            </div>

            {/* Phone (Optional) */}
            <div>
              <Label htmlFor="phone" className="text-[#94A3B8] text-xs uppercase tracking-wider font-medium mb-2 block">
                Phone Number (Optional)
              </Label>
              <div className="relative">
                <Phone className="absolute left-3 top-3.5 w-4 h-4 text-[#475569]" />
                <Input
                  id="phone"
                  data-testid="register-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+66 XX XXX XXXX"
                  className="pl-10 bg-[#0A0E27] border-white/10 text-white placeholder:text-[#475569] focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 h-12 rounded-xl"
                />
              </div>
            </div>

            <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/15 rounded-xl p-4 text-sm text-[#94A3B8]">
              Please provide either Line ID or WhatsApp number so we can contact you.
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#D4AF37] hover:bg-[#E2C364] text-[#0A0E27] font-semibold h-12 rounded-full text-sm tracking-wide transition-colors duration-300"
              data-testid="register-submit-button"
            >
              {loading ? 'Registering...' : 'Create Account'}
            </Button>

            <p className="text-center text-sm text-[#94A3B8]">
              Already registered?{' '}
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-[#D4AF37] hover:text-[#E2C364] transition-colors duration-300"
                data-testid="login-link"
              >
                Sign in
              </button>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ClientRegister;
